import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, Form } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import UserSelector from '../../components/UserSelector';
import styles from './index.module.scss';

const Channels = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [selectedUser, setSelectedUser] = useState([]);

    const statusMap = {
        0: { text: '未审核', type: 'warning' },
        1: { text: '审核通过', type: 'success' },
        2: { text: '审核拒绝', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_channel/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    useEffect(() => {
        if (currentRecord?.su_uid) {
            setSelectedUser([currentRecord.su_uid]);
        } else {
            setSelectedUser([]);
        }
    }, [currentRecord]);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/all/add_all_channel/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/all/edit_all_channel/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_channel/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的渠道');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/batchedit_all_channel/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const columns = [
        {
            title: '渠道名称',
            dataIndex: 'name',
            width: 200,
        },
        {
            title: '绑定用户',
            dataIndex: 'su',
            width: 150,
            render: (su) => su?.name || '-'
        },
        {
            title: '父级渠道',
            dataIndex: 'parent',
            width: 150,
            render: (parent) => parent?.name || '-'
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '分佣比例',
            dataIndex: 'rate',
            width: 120,
            render: (text) => `${text}%`
        },
        {
            title: '备注',
            dataIndex: 'sp_remark',
            width: 200,
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{record.create_time}</div>
                    <div>{text}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该渠道吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            su_uid: currentRecord.su_uid,
            parent_uid: currentRecord.parent_uid,
            name: currentRecord.name,
            status: currentRecord.status,
            rate: currentRecord.rate,
            sp_remark: currentRecord.sp_remark
        } : {
            su_uid: '',
            parent_uid: '',
            name: '',
            status: 0,
            rate: 0,
            sp_remark: ''
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="name"
                    label="渠道名称"
                    rules={[{ required: true, message: '请输入渠道名称' }]}
                />
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>绑定用户</div>
                    <UserSelector
                        value={selectedUser}
                        onChange={(value) => {
                            const newValue = value.length > 0 ? [value[value.length - 1]] : [];
                            setSelectedUser(newValue);
                            formApi.setValue('su_uid', newValue.length > 0 ? newValue[0] : '');
                        }}
                        placeholder="请选择用户（选填）"
                    />
                </div>
                <Form.Input
                    field="parent_uid"
                    label="父级渠道UID"
                    placeholder="选填"
                />
                <Form.Select
                    field="status"
                    label="状态"
                    initValue={0}
                >
                    {Object.entries(statusMap).map(([value, { text }]) => (
                        <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                    ))}
                </Form.Select>
                <Form.InputNumber
                    field="rate"
                    label="分佣比例(%)"
                    min={0}
                    max={100}
                    rules={[{ required: true, message: '请输入分佣比例' }]}
                />
                <Form.Input
                    field="sp_remark"
                    label="备注"
                    placeholder="选填"
                />
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.channelsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索渠道名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="渠道状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增渠道</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量未审核</Button>
                            <Button type='success' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量通过</Button>
                            <Button type='danger' onClick={() => handleBatchEdit(2, selectedRowKeys)}>批量拒绝</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个渠道吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增渠道"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑渠道"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default Channels; 